<template>
  <div class="is-inline-flex is-align-items-center is-text-not-selectable">
    <div
      class="is-inline-flex m-l-2 is-relative xy-t-3"
      :data-e2e="AICypressAttribute.views.CLARA_CITATION_PILL"
      @mouseenter="onSourcePanelEnter"
      @mouseleave="onSourcePanelLeave"
    >
      <FloatingPanelView
        :is-open="isSourcePanelOpen"
        :append-to-body="true"
        :offset-value="0"
      >
        <template #trigger>
          <ClaraCitationPill :document="firstDocument" />
        </template>
        <div
          class="is-default-theme"
          :data-e2e="AICypressAttribute.views.CLARA_CITATION_SOURCE_PANEL"
          @mouseenter="onSourcePanelEnter"
          @mouseleave="onSourcePanelLeave"
        >
          <ClaraSource
            :document="firstDocument"
            :source="firstSimilarityData"
            width="250px"
            :has-border="true"
          />
        </div>
      </FloatingPanelView>
    </div>

    <div
      v-if="hasAdditionalCitations"
      class="is-inline-flex m-l-2 is-relative xy-t-2"
      :data-e2e="AICypressAttribute.views.CLARA_GROUPED_CITATION_PILL"
      @mouseenter="onNavigatorPanelEnter"
      @mouseleave="onNavigatorPanelLeave"
    >
      <FloatingPanelView
        :is-open="isNavigatorPanelOpen"
        :append-to-body="true"
        :offset-value="0"
      >
        <template #trigger>
          <TagView>
            <div
              class="is-flex is-align-items-center is-clickable grouped-citation-wrapper is-radius-medium px-1 has-text-grey is-full-height"
            >
              <span class="grouped-citation-count has-text-weight-medium is-size-7 px-1">
                +{{ additionalCitationsCount }}
              </span>
            </div>
          </TagView>
        </template>
        <div
          class="is-default-theme"
          :data-e2e="AICypressAttribute.views.CLARA_GROUPED_CITATION_SOURCE_PANEL"
          @mouseenter="onNavigatorPanelEnter"
          @mouseleave="onNavigatorPanelLeave"
        >
          <ClaraSourceNavigator
            :documents="additionalDocuments"
            :similarity-data="additionalSimilarityData"
            :initial-index="0"
            @navigation-change="onNavigationChange"
          />
        </div>
      </FloatingPanelView>
    </div>
  </div>
</template>

<script>
import ClaraSource from '@/modules/ai/components/presentational/ClaraSource.vue';
import FloatingPanelView from '@/modules/core/ui/components/wrappers/FloatingPanelView.vue';
import TagView from '@/modules/core/ui/components/wrappers/TagView.vue';
import ClaraSourceNavigator from '@/modules/ai/components/presentational/ClaraSourceNavigator.vue';
import ClaraCitationPill from '@/modules/ai/components/presentational/ClaraCitationPill.vue';
import { AICypressAttribute } from '@/modules/ai/constants/AICypressAttribute';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'ClaraCitation',
  components: {
    FloatingPanelView,
    ClaraSource,
    ClaraCitationPill,
    TagView,
    ClaraSourceNavigator
  },
  props: {
    documents: {
      type: Array,
      required: true
    },
    indices: {
      type: Array,
      required: true
    },
    similarityData: {
      type: Array,
      required: true
    }
  },
  static: {
    AICypressAttribute
  },
  data() {
    return {
      isSourcePanelOpen: false,
      isNavigatorPanelOpen: false,
      closeTimeoutIds: {},
      currentNavigationIndex: 0
    };
  },
  computed: {
    hasAdditionalCitations() {
      return this.documents.length > 1;
    },
    additionalCitationsCount() {
      return this.documents.length - 1;
    },
    firstDocument() {
      return this.documents[0];
    },
    firstIndex() {
      return this.indices[0];
    },
    firstSimilarityData() {
      return this.hasAdditionalCitations
        ? this.similarityData[0]
        : this.similarityData[this.indices[0] - 1];
    },
    additionalDocuments() {
      return this.documents.slice(1);
    },
    additionalSimilarityData() {
      return this.similarityData.slice(1);
    }
  },
  methods: {
    onSourcePanelEnter() {
      this.openPanel('isSourcePanelOpen');
    },
    onSourcePanelLeave() {
      this.closePanel('isSourcePanelOpen');
    },
    onNavigatorPanelEnter() {
      this.openPanel('isNavigatorPanelOpen');
    },
    onNavigatorPanelLeave() {
      this.closePanel('isNavigatorPanelOpen');
    },
    openPanel(stateKey) {
      clearTimeout(this.closeTimeoutIds[stateKey]);
      this[stateKey] = true;
    },
    closePanel(stateKey) {
      this.closeTimeoutIds[stateKey] = setTimeout(() => {
        this[stateKey] = false;
      });
    },
    onNavigationChange(navigationIndex) {
      this.currentNavigationIndex = navigationIndex;
    }
  },
  beforeUnmount() {
    Object.values(this.closeTimeoutIds).forEach(clearTimeout);
  }
};
</script>

<style scoped>
span.tag {
  padding: 0 !important;
  position: relative;
  bottom: var(--ce-2px);
  transition: all 0.3s ease;

  .grouped-citation-wrapper {
    height: var(--ce-20px);
  }

  &:hover {
    background-color: var(--ce-primary-light-30) !important;
    span {
      color: var(--ce-black) !important;
    }
  }
}

:deep(.source-content-container) {
  max-height: unset !important;
}
</style>
